import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { t } from '../i18n'
import { api } from '../Services/api'
import { googleIdToken, loginErrorKind, sendEmailLink } from '../Services/login'
import {
  accountExpired,
  commentToken,
  finishLogin,
  logout,
  saveCommentName,
  savedCommentName,
  setAccountName,
  useAccount,
  useCommentVote,
  useDiscussions,
  withCommentToken,
} from '../State/Discussions'
import { useUi } from '../State/Ui'
import { track } from '../Utils/analytics'
import { KEYS, readJSON, writeJSON } from '../Utils/storage'
import { timeAgo } from '../Utils/time'
import { ChatIcon, ChevronRight, CloseIcon, Spinner, ThumbIcon } from './Icons'
import { SourceAvatar } from './SourceAvatar'

/* Komentari (design: "KOMENTARI: panel s dna"). A story has one thread: a
   comment is written under one article (Klix's, Avaz's, …) and says so, but
   readers of every source's version of the story read and answer each other
   in one place. The panel is a sheet from the bottom on phones and a window
   on desktop; the Glavna vijest shows the story's best comment. */

export const COMMENTS_HASH = '#komentari' // /prica/{id}#komentari opens the panel
const BODY_MAX = 1000
const NAME_MAX = 30
const AVATAR_COLORS = ['#3B6E8F', '#6B5B95', '#4E7D5B', '#8B5A2B', '#9A4A5A', '#2F7A78', '#5B6B8F', '#7A6A2F']
const MY_COLOR = '#F0801F'

export function initials(name) {
  const words = String(name || '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean)
  if (!words.length) return '?'
  return (words[0][0] + (words[1]?.[0] || '')).toUpperCase()
}

function avatarColor(name) {
  let h = 0
  for (const ch of String(name)) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  return AVATAR_COLORS[h % AVATAR_COLORS.length]
}

export function CommentAvatar({ name, size = 32, me = false }) {
  return (
    <span
      className="comment-avatar"
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.35),
        background: me ? MY_COLOR : avatarColor(name),
      }}
    >
      {initials(name)}
    </span>
  )
}

/* 👍 and 👎 with their counts. Tapping your own vote again takes it back. */
function VoteButtons({ comment, likeOnly = false, size = 14 }) {
  const { notify } = useUi()
  const { vote, likes, dislikes, cast } = useCommentVote(comment)
  const tap = (value) => (e) => {
    e.stopPropagation()
    track('comment_vote', { value: vote === value ? 0 : value })
    cast(value).catch(() => notify(t('comments.actionError')))
  }
  return (
    <>
      <button
        type="button"
        className={`vote-button ${vote === 1 ? 'on' : ''}`}
        aria-pressed={vote === 1}
        aria-label={t('comments.like')}
        onClick={tap(1)}
      >
        <ThumbIcon size={size} active={vote === 1} strokeWidth={1.8} />
        {likes}
      </button>
      {!likeOnly && (
        <button
          type="button"
          className={`vote-button ${vote === -1 ? 'on' : ''}`}
          aria-pressed={vote === -1}
          aria-label={t('comments.dislike')}
          onClick={tap(-1)}
        >
          <ThumbIcon size={size} active={vote === -1} down strokeWidth={1.8} />
          {dislikes > 0 && dislikes}
        </button>
      )}
    </>
  )
}

/* On a card: the comment count; opens the panel. */
export function CommentButton({ story, from }) {
  const { openComments } = useUi()
  if (story.commentCount == null) return null
  return (
    <button
      type="button"
      className="comment-button"
      aria-label={t('comments.open', { count: story.commentCount })}
      onClick={(e) => {
        e.stopPropagation()
        track('comments_open', { from })
        openComments({ storyId: story.id, articleId: story.articleId, count: story.commentCount })
      }}
    >
      <ChatIcon size={19} strokeWidth={1.8} />
      {story.commentCount > 0 && <span>{story.commentCount}</span>}
    </button>
  )
}

/* Under the Glavna vijest: its best comment ("najpopularniji komentar"). */
export function BestComment({ story, from }) {
  const { openComments, isMobile } = useUi()
  const c = story.topComment
  if (!c) return null
  const open = () => {
    track('comments_open', { from, via: 'best_comment' })
    openComments({ storyId: story.id, articleId: story.articleId, count: story.commentCount })
  }
  return (
    // The whole box opens the panel; its "Pogledaj…" button is the keyboard way in.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="best-comment" onClick={open}>
      <CommentAvatar name={c.author} size={isMobile ? 24 : 32} />
      <div className="best-comment-main">
        <div className="best-comment-head">
          <span className="best-comment-who">
            <strong>{c.author}</strong>
            {' · '}
            {c.likes > c.dislikes ? t('comments.mostPopular') : t('comments.newest')}
          </span>
          <span className="best-comment-votes">
            <VoteButtons comment={c} likeOnly size={isMobile ? 15 : 18} />
          </span>
        </div>
        <p className="best-comment-body">{c.body}</p>
        <button
          type="button"
          className="best-comment-more"
          onClick={(e) => {
            e.stopPropagation()
            open()
          }}
        >
          {story.commentCount > 1
            ? t('comments.viewAll', { count: story.commentCount })
            : t('comments.viewOne')}
        </button>
      </div>
    </div>
  )
}

function errorText(error, fallback) {
  const status = error?.response?.status
  const message = error?.response?.data?.error
  // 400/429 carry a sentence written for the reader.
  return (status === 400 || status === 429) && message ? message : fallback
}

function Comment({ comment: c, onReply, onDelete, onReport }) {
  return (
    <li className="comment">
      <CommentAvatar name={c.author} size={32} me={c.mine} />
      <div className="comment-main">
        <div className="comment-meta">
          <strong>{c.author}</strong>
          {c.mine && <span className="tag-following">{t('comments.you')}</span>}
          <span className="comment-faint">· {timeAgo(c.createdAt)}</span>
          {/* Where it was written: the article (and source) it was posted under. */}
          <a
            className="comment-origin"
            href={c.article.url}
            target="_blank"
            rel="noopener noreferrer"
            title={c.article.title}
            aria-label={t('comments.originLabel', { name: c.article.source.name, title: c.article.title })}
          >
            <span className="comment-faint">· {t('comments.under')}</span>
            <SourceAvatar source={c.article.source} size={14} />
            <span className="comment-origin-name">{c.article.source.name}</span>
          </a>
        </div>
        <p className="comment-text">{c.body}</p>
        <div className="comment-actions">
          <VoteButtons comment={c} />
          <button type="button" className="comment-action strong" onClick={() => onReply(c)}>
            {t('comments.reply')}
          </button>
          {c.mine ? (
            <button type="button" className="comment-action faint" onClick={() => onDelete(c)}>
              {t('comments.delete')}
            </button>
          ) : (
            <button type="button" className="comment-action faint" onClick={() => onReport(c)}>
              {t('comments.report')}
            </button>
          )}
        </div>
      </div>
    </li>
  )
}

/* The name others see next to your comments. Without login it stays in this
   browser; with login it's the account's (and changes your past comments). */
function NamePrompt({ initial, onSave, account }) {
  const [value, setValue] = useState(initial || '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const clean = value.trim()
  const loggingOut = useRef(false)
  return (
    <form
      className="name-prompt"
      onSubmit={async (e) => {
        e.preventDefault()
        if (!clean || busy) return
        setBusy(true)
        setError(null)
        try {
          await onSave(clean.slice(0, NAME_MAX))
        } catch (err) {
          setError(errorText(err, t('comments.actionError')))
          setBusy(false)
        }
      }}
    >
      <div className="name-prompt-text">
        <strong>{t('comments.nameTitle')}</strong>
        <span>{t(account ? 'comments.nameTextAccount' : 'comments.nameText')}</span>
      </div>
      <input
        className="name-input"
        value={value}
        maxLength={NAME_MAX}
        placeholder={t('comments.name')}
        aria-label={t('comments.name')}
        autoComplete="nickname"
        onChange={(e) => setValue(e.target.value)}
      />
      <button type="submit" className="name-continue" disabled={!clean || busy}>
        {busy ? <Spinner size={16} /> : t('comments.nameContinue')}
      </button>
      {error && (
        <p className="composer-error" role="alert">
          {error}
        </p>
      )}
      {account && (
        <p className="login-account">
          {t('login.account', { email: account.email || '' })}
          {' · '}
          <button
            type="button"
            className="link-button"
            onClick={async () => {
              if (loggingOut.current) return
              loggingOut.current = true
              await logout()
              track('logout', {})
            }}
          >
            {t('login.logout')}
          </button>
        </p>
      )}
    </form>
  )
}

const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
)

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/* Design: "Prijavi se da komentarišeš". Google (a popup) or a link by email
   (it comes back to /prijava, which reopens these comments). */
function LoginPrompt({ storyId, articleId }) {
  const { notify } = useUi()
  const location = useLocation()
  const [mode, setMode] = useState('choose') // choose | email | sent
  const [email, setEmail] = useState(() => readJSON(KEYS.loginEmail, ''))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const fail = (err) => {
    const kind = loginErrorKind(err)
    if (kind !== 'cancelled') setError(t(`login.errors.${kind === 'expired' ? 'other' : kind}`))
  }
  const google = async () => {
    setBusy(true)
    setError(null)
    try {
      await finishLogin(await googleIdToken())
      notify(t('login.welcome'))
      track('login', { method: 'google' })
    } catch (err) {
      fail(err)
    } finally {
      setBusy(false)
    }
  }
  const sendLink = async (e) => {
    e?.preventDefault()
    const address = email.trim()
    if (!EMAIL_RE.test(address)) {
      setError(t('login.errors.email'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      await sendEmailLink(address)
      writeJSON(KEYS.loginEmail, address)
      writeJSON(KEYS.loginReturn, { path: location.pathname + location.search, storyId, articleId })
      setMode('sent')
      track('login_link_sent', {})
    } catch (err) {
      fail(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="name-prompt login-prompt">
      <div className="name-prompt-text">
        <strong>{t('login.promptTitle')}</strong>
        <span>{t('login.promptText')}</span>
      </div>
      {mode === 'choose' && (
        <div className="login-buttons">
          <button type="button" className="login-google" disabled={busy} onClick={google}>
            {busy ? <Spinner size={16} /> : <GoogleLogo />}
            {t('login.google')}
          </button>
          <button type="button" className="login-email" onClick={() => setMode('email')}>
            {t('login.email')}
          </button>
        </div>
      )}
      {mode === 'email' && (
        <form className="login-buttons" onSubmit={sendLink} noValidate>
          <input
            className="name-input"
            type="email"
            value={email}
            placeholder={t('login.emailPlaceholder')}
            aria-label={t('login.emailLabel')}
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
          />
          <button type="submit" className="name-continue" disabled={busy || !email.trim()}>
            {busy ? <Spinner size={16} /> : t('login.sendLink')}
          </button>
          <button type="button" className="login-email" onClick={() => setMode('choose')}>
            {t('login.back')}
          </button>
        </form>
      )}
      {mode === 'sent' && (
        <div className="login-buttons">
          <p className="login-sent">{t('login.sent', { email: email.trim() })}</p>
          <button type="button" className="login-email" disabled={busy} onClick={() => sendLink()}>
            {t('login.resend')}
          </button>
          <button type="button" className="login-email" onClick={() => setMode('email')}>
            {t('login.otherEmail')}
          </button>
        </div>
      )}
      {error && (
        <p className="composer-error login-error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

function Composer({ storyId, articles, defaultArticleId, draft, setDraft, inputRef, onPosted }) {
  const { login: loginOn } = useDiscussions()
  const account = useAccount()
  const [localName, setLocalName] = useState(savedCommentName)
  const name = loginOn ? account?.name || '' : localName
  const [editingName, setEditingName] = useState(false)
  const [articleId, setArticleId] = useState(() =>
    articles.some((a) => a.id === defaultArticleId) ? defaultArticleId : articles[0]?.id
  )
  const [posting, setPosting] = useState(false)
  const [error, setError] = useState(null)
  const article = articles.find((a) => a.id === articleId) || articles[0]
  const ready = draft.trim().length >= 2 && !posting

  if (loginOn && !account) return <LoginPrompt storyId={storyId} articleId={article?.id} />
  if (editingName || !name) {
    return (
      <NamePrompt
        initial={name}
        account={loginOn ? account : null}
        onSave={async (value) => {
          if (loginOn) await setAccountName(value)
          else {
            saveCommentName(value)
            setLocalName(value)
          }
          setEditingName(false)
          setTimeout(() => inputRef.current?.focus(), 0)
        }}
      />
    )
  }

  const submit = async (e) => {
    e.preventDefault()
    if (!ready) return
    setPosting(true)
    setError(null)
    try {
      const comment = await withCommentToken((token) =>
        api.postComment(storyId, { articleId: article.id, name, body: draft }, token)
      )
      setDraft('')
      onPosted(comment)
      track('comment_post', { source: article.source.slug, length: draft.length })
    } catch (err) {
      if (err?.response?.status === 403) accountExpired() // not logged in after all
      else setError(errorText(err, t('comments.postError')))
    } finally {
      setPosting(false)
    }
  }

  return (
    <form className="composer" onSubmit={submit}>
      <div className="composer-row">
        <button
          type="button"
          className="composer-avatar"
          title={t('comments.changeName')}
          aria-label={`${t('comments.changeName')}: ${name}`}
          onClick={() => setEditingName(true)}
        >
          <CommentAvatar name={name} size={32} me />
        </button>
        <label className="composer-field">
          <span className="sr-only">{t('comments.placeholder')}</span>
          <input
            ref={inputRef}
            type="text"
            value={draft}
            maxLength={BODY_MAX}
            placeholder={t('comments.placeholder')}
            onChange={(e) => setDraft(e.target.value)}
          />
        </label>
        <button type="submit" className={`composer-post ${ready ? 'ready' : ''}`} disabled={!ready}>
          {posting ? <Spinner size={14} /> : t('comments.submit')}
        </button>
      </div>
      <p className="composer-note">
        {articles.length > 1 && (
          <span className="composer-under">
            {t('comments.postingUnder')}{' '}
            {/* The chosen portal as text; the invisible select on top picks it. */}
            <span className="composer-pick">
              <strong>{article.source.name}</strong>
              <ChevronRight size={12} strokeWidth={2.6} className="composer-pick-icon" />
              <select
                value={article.id}
                aria-label={t('comments.underArticleLabel')}
                onChange={(e) => setArticleId(Number(e.target.value))}
              >
                {articles.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.source.name}
                  </option>
                ))}
              </select>
            </span>
            {' · '}
          </span>
        )}
        {t('comments.note')}
      </p>
      {error && (
        <p className="composer-error" role="alert">
          {error}
        </p>
      )}
    </form>
  )
}

function Panel({ storyId, articleId, count, onClose }) {
  const { notify, isMobile } = useUi()
  const [state, setState] = useState({ status: 'loading', data: null })
  const [attempt, setAttempt] = useState(0)
  const [draft, setDraft] = useState('')
  const sheet = useRef(null)
  const list = useRef(null)
  const input = useRef(null)

  useEffect(() => {
    let alive = true
    setState({ status: 'loading', data: null })
    api
      .storyComments(storyId, commentToken())
      .then((data) => alive && setState({ status: 'ready', data }))
      .catch(() => alive && setState({ status: 'error', data: null }))
    return () => {
      alive = false
    }
  }, [storyId, attempt])

  useEffect(() => {
    const previous = document.activeElement
    sheet.current?.focus()
    const onKey = (e) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    document.body.classList.add('no-scroll')
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.classList.remove('no-scroll')
      previous?.focus?.()
    }
  }, [onClose])

  const comments = state.data?.comments || []
  const update = (fn) =>
    setState((s) => ({ ...s, data: { ...s.data, comments: fn(s.data.comments) } }))

  const reply = (c) => {
    setDraft((d) => (d.startsWith(`@${c.author} `) ? d : `@${c.author} ${d}`))
    setTimeout(() => input.current?.focus(), 0)
  }
  const remove = async (c) => {
    if (!window.confirm(t('comments.confirmDelete'))) return
    try {
      await api.deleteComment(c.id, commentToken())
      update((all) => all.filter((x) => x.id !== c.id))
      notify(t('comments.deleted'))
    } catch (e) {
      notify(t('comments.actionError'))
    }
  }
  const report = async (c) => {
    try {
      await withCommentToken((token) => api.reportComment(c.id, token))
      update((all) => all.filter((x) => x.id !== c.id)) // hidden for whoever reported it
      notify(t('comments.reported'))
      track('comment_report', {})
    } catch (e) {
      notify(t('comments.actionError'))
    }
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="sheet-backdrop" onClick={onClose}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <section
        ref={sheet}
        className={`comments-sheet ${isMobile ? 'from-bottom' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="comments-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {isMobile && <span className="sheet-grabber" aria-hidden="true" />}
        <header className="sheet-head">
          <h2 id="comments-title">
            {t('comments.title')}{' '}
            <span className="sheet-count">{state.data ? comments.length : count || ''}</span>
          </h2>
          <button
            type="button"
            className="sheet-close"
            aria-label={t('comments.close')}
            onClick={onClose}
          >
            <CloseIcon size={16} strokeWidth={2.2} />
          </button>
        </header>

        <div className="sheet-body" ref={list}>
          {state.status === 'loading' && (
            <div className="sheet-message">
              <Spinner size={22} />
            </div>
          )}
          {state.status === 'error' && (
            <div className="sheet-message">
              <p>{t('comments.loadError')}</p>
              <button
                type="button"
                className="btn btn-secondary btn-small"
                onClick={() => setAttempt((n) => n + 1)}
              >
                {t('common.retry')}
              </button>
            </div>
          )}
          {state.status === 'ready' && comments.length === 0 && (
            <div className="sheet-message">
              <ChatIcon size={28} className="muted-icon" />
              <p>{t('comments.empty')}</p>
            </div>
          )}
          {comments.length > 0 && (
            <ul className="comment-list">
              {comments.map((c) => (
                <Comment key={c.id} comment={c} onReply={reply} onDelete={remove} onReport={report} />
              ))}
            </ul>
          )}
        </div>

        {state.status === 'ready' && (
          <footer className="sheet-foot">
            <Composer
              storyId={storyId}
              articles={state.data.articles}
              defaultArticleId={articleId}
              draft={draft}
              setDraft={setDraft}
              inputRef={input}
              onPosted={(comment) => {
                update((all) => [comment, ...all])
                list.current?.scrollTo({ top: 0, behavior: 'smooth' })
              }}
            />
          </footer>
        )}
      </section>
    </div>
  )
}

/* The panel itself, once for the whole site (Layout). */
export function CommentsSheet() {
  const { comments, closeComments } = useUi()
  const { enabled } = useDiscussions()
  if (!enabled || !comments) return null
  return <Panel key={comments.storyId} {...comments} onClose={closeComments} />
}

/* /prica/{id}#komentari (Diskusije, shared links): open the story's panel. */
export function useCommentsLink(story) {
  const { openComments } = useUi()
  const { enabled } = useDiscussions()
  const location = useLocation()
  const navigate = useNavigate()
  const storyId = story?.id
  useEffect(() => {
    if (!enabled || !storyId || location.hash !== COMMENTS_HASH) return
    openComments({ storyId, articleId: location.state?.articleId ?? story.articleId })
    // Closing the panel shouldn't reopen it: drop the hash.
    navigate(`${location.pathname}${location.search}`, { replace: true, state: location.state })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, storyId, location.hash])
}
